import lpWriterPack from "../../../../../../knowledge/agents/lp-writer/pack.json";

export type LandingPageInput = {
	industry: string;
	product: string;
	objective: string;
	tone: string;
};

export type LandingPageSectionType =
	| "hero"
	| "problem"
	| "solution"
	| "benefits"
	| "evidence"
	| "how-it-works"
	| "faq"
	| "final-cta";

export type LandingPageSection = {
	id: string;
	type: LandingPageSectionType;
	headline: string;
	body: string;
	cta?: string;
};

export type LandingPageOutput = {
	ucsId: "UCS-WEB-001";
	input: LandingPageInput;
	knowledgePacks: Array<{ name: string; version: string }>;
	strategy: {
		targetAudience: string;
		corePromise: string;
		primaryCta: string;
	};
	sections: LandingPageSection[];
};

export interface LandingPageGenerator {
	generate(input: LandingPageInput): Promise<LandingPageOutput>;
}

const knowledgePacks = [
	{ name: lpWriterPack.name, version: lpWriterPack.version },
	...lpWriterPack.uses.map(({ pack, version }) => ({ name: pack, version })),
];

function clean(input: LandingPageInput): LandingPageInput {
	return {
		industry: input.industry.trim(),
		product: input.product.trim(),
		objective: input.objective.trim(),
		tone: input.tone.trim(),
	};
}

export const mockLandingPageGenerator: LandingPageGenerator = {
	async generate(rawInput) {
		const input = clean(rawInput);
		const primaryCta = `${input.objective}ための相談を始める`;
		const corePromise = `${input.product}で、${input.objective}一歩をつくります`;

		return {
			ucsId: "UCS-WEB-001",
			input,
			knowledgePacks,
			strategy: {
				targetAudience: `${input.industry}で成果につながる次の一手を探している方`,
				corePromise,
				primaryCta,
			},
			sections: [
				{
					id: "hero",
					type: "hero",
					headline: corePromise,
					body: `${input.tone}トーンで、必要な情報を迷わず確認できるLPをご提案します。`,
					cta: primaryCta,
				},
				{
					id: "problem",
					type: "problem",
					headline: "伝えたいことが多いほど、選ぶ理由は見えにくくなります",
					body: `${input.industry}のお客さまが判断に必要な情報を、課題と利用場面から整理します。`,
				},
				{
					id: "solution",
					type: "solution",
					headline: "価値を、次の行動までつながる順番で伝えます",
					body: `${input.product}の特徴を、読み手が得られる変化と利用の流れに置き換えます。`,
				},
				{
					id: "benefits",
					type: "benefits",
					headline: "知る・納得する・動くを、一つのページで",
					body: `対象を明確にし、便益と条件を揃え、${input.objective}行動まで迷いを減らします。`,
				},
				{
					id: "evidence",
					type: "evidence",
					headline: "判断材料は、確認できる事実から",
					body: "実績・数値・お客さまの声は、提供された根拠がある情報だけを掲載します。",
				},
				{
					id: "how-it-works",
					type: "how-it-works",
					headline: "相談から公開準備まで、3つのステップ",
					body: "目的を確認し、構成とコピーをすり合わせ、条件を確認して公開準備へ進みます。",
				},
				{
					id: "faq",
					type: "faq",
					headline: "始める前の疑問を解消します",
					body: "必要な素材、進め方、公開までの確認事項を事前に整理してご案内します。",
				},
				{
					id: "final-cta",
					type: "final-cta",
					headline: `${input.objective}次の一歩を、ここから`,
					body: "まずは目的と現在の状況をお聞かせください。必要な情報を一緒に整理します。",
					cta: primaryCta,
				},
			],
		};
	},
};
